"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FiCheck } from "react-icons/fi";
import { XIcon } from "./icons/XIcon";
import { CopyIcon } from "./icons/CopyIcon";
import { DownloadIcon } from "./icons/DownloadIcon";
import { FaXTwitter, FaTelegram } from "react-icons/fa6";
import { authFetch } from "@/lib/authFetch";
import { useT } from "@/hooks/useT";

interface StatsData {
  username: string;
  userAvatar?: string;
  totalVolume: number;
  totalTrades: number;
  totalPnL: number;
  unrealizedPnL: number;
  realizedPnL: number;
  roi: number;
  openPositions: number;
  portfolioValue: number;
  winCount?: number;
  lossCount?: number;
  winRate?: number;
  bestTrade?: number;
}

type PeriodType = 'today' | 'week' | 'month' | 'all_time';

interface ShareStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  stats: StatsData;
  preGeneratedImageUrl?: string; // Pre-generated image to avoid double generation
}

export default function ShareStatsModal({
  isOpen,
  onClose,
  stats,
  preGeneratedImageUrl,
}: ShareStatsModalProps) {
  const t = useT();
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loadingCard, setLoadingCard] = useState(true);
  const [cardImageUrl, setCardImageUrl] = useState<string | null>(null);
  const [cardBlob, setCardBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('all_time');

  // Button hover states
  const [copyHovered, setCopyHovered] = useState(false);
  const [downloadHovered, setDownloadHovered] = useState(false);

  // 3D Card rotation state
  const [cardRotation, setCardRotation] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const [isAutoRotating, setIsAutoRotating] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef({ x: 0, y: 0, rotX: 0, rotY: 0 });

  // Reset 3D rotation when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCardRotation({ x: 0, y: 0 });
      setIsDragging(false);
      setHasDragged(false);
      setIsAutoRotating(false);
    }
  }, [isOpen]);

  // Handle 3D card drag rotation
  const handleCardMouseDown = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true);
    setHasDragged(false);
    setIsAutoRotating(false);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragStartRef.current = {
      x: clientX,
      y: clientY,
      rotX: cardRotation.x,
      rotY: cardRotation.y,
    };
  }, [cardRotation]);

  const handleCardMouseMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isDragging) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const deltaX = clientX - dragStartRef.current.x;
    const deltaY = clientY - dragStartRef.current.y;

    if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
      setHasDragged(true);
    }

    const sensitivity = 0.3;
    setCardRotation({
      x: dragStartRef.current.rotX - deltaY * sensitivity,
      y: dragStartRef.current.rotY + deltaX * sensitivity,
    });
  }, [isDragging]);

  const handleCardMouseUp = useCallback(() => {
    setIsDragging(false);
    setHasDragged(false);
  }, []);

  // Auto-rotate animation on double-click
  const handleCardDoubleClick = useCallback(() => {
    setIsAutoRotating(true);
    const startRotation = cardRotation.y;
    const startTime = Date.now();
    const duration = 1000;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeInOut = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      setCardRotation(prev => ({
        x: prev.x * (1 - easeInOut),
        y: startRotation + 360 * easeInOut,
      }));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setIsAutoRotating(false);
        setCardRotation({ x: 0, y: 0 });
      }
    };
    requestAnimationFrame(animate);
  }, [cardRotation.y]);

  // Add/remove global listeners for drag
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleCardMouseMove);
      window.addEventListener('mouseup', handleCardMouseUp);
      window.addEventListener('touchmove', handleCardMouseMove);
      window.addEventListener('touchend', handleCardMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleCardMouseMove);
      window.removeEventListener('mouseup', handleCardMouseUp);
      window.removeEventListener('touchmove', handleCardMouseMove);
      window.removeEventListener('touchend', handleCardMouseUp);
    };
  }, [isDragging, handleCardMouseMove, handleCardMouseUp]);

  // Generate card for a given period with optional period-specific stats
  const generateCard = useCallback(async (period: PeriodType, statsOverride?: StatsData) => {
    setLoadingCard(true);
    setError(null);

    const s = statsOverride || stats;

    try {
      const response = await fetch('/api/pnl-card/stats', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: s.username,
          userAvatar: s.userAvatar,
          period,
          totalVolume: s.totalVolume,
          totalTrades: s.totalTrades,
          totalPnl: s.totalPnL,
          unrealizedPnl: s.unrealizedPnL,
          realizedPnl: s.realizedPnL,
          roi: s.roi,
          openPositions: s.openPositions,
          portfolioValue: s.portfolioValue,
          winCount: s.winCount || 0,
          lossCount: s.lossCount || 0,
          winRate: s.winRate || 0,
          bestTrade: s.bestTrade || 0,
        }),
      });

      if (!response.ok) throw new Error('Failed to generate card');

      const blob = await response.blob();
      setCardBlob(blob);

      if (cardImageUrl) URL.revokeObjectURL(cardImageUrl);
      setCardImageUrl(URL.createObjectURL(blob));
    } catch {
      setError(t("share.stats.generationFailed"));
    } finally {
      setLoadingCard(false);
    }
  }, [stats, cardImageUrl]);

  // Fetch period-specific stats and regenerate card
  const handlePeriodChange = useCallback(async (period: PeriodType) => {
    setSelectedPeriod(period);

    // For all_time, use the stats prop directly
    if (period === 'all_time') {
      generateCard(period);
      return;
    }

    // Fetch period-filtered stats
    setLoadingCard(true);
    try {
      const res = await authFetch(`/api/portfolio/all?period=${period}`, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch period stats');
      const data = await res.json();
      const ps = data.stats || {};

      generateCard(period, {
        ...stats,
        totalPnL: ps.totalPnL || 0,
        unrealizedPnL: ps.unrealizedPnl || 0,
        realizedPnL: ps.realizedPnl || 0,
        roi: ps.roi || 0,
        winCount: ps.winCount || 0,
        lossCount: ps.lossCount || 0,
        winRate: ps.winRate || 0,
        bestTrade: ps.bestTrade || 0,
      });
    } catch {
      setError(t("share.stats.fetchFailed"));
      setLoadingCard(false);
    }
  }, [stats, generateCard]);

  // Generate card when modal opens, reset when it closes
  useEffect(() => {
    if (!isOpen) {
      if (cardImageUrl) URL.revokeObjectURL(cardImageUrl);
      setCardImageUrl(null);
      setCardBlob(null);
      setError(null);
      setSelectedPeriod('all_time');
      return;
    }

    // If we have a pre-generated image, use it directly
    if (preGeneratedImageUrl) {
      setCardImageUrl(preGeneratedImageUrl);
      fetch(preGeneratedImageUrl)
        .then(res => res.blob())
        .then(blob => setCardBlob(blob))
        .catch(() => {
          if (preGeneratedImageUrl.startsWith('data:')) {
            const byteString = atob(preGeneratedImageUrl.split(',')[1]);
            const mimeString = preGeneratedImageUrl.split(',')[0].split(':')[1].split(';')[0];
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);
            for (let i = 0; i < byteString.length; i++) {
              ia[i] = byteString.charCodeAt(i);
            }
            setCardBlob(new Blob([ab], { type: mimeString }));
          }
        });
      setLoadingCard(false);
      return;
    }

    generateCard('all_time');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, preGeneratedImageUrl]);

  const handleCopyImage = useCallback(async () => {
    if (!cardBlob) return;

    setCopying(true);
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "image/png": cardBlob,
        }),
      ]);

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      alert(t("share.stats.copyError") + " " + (err as Error).message);
    } finally {
      setCopying(false);
    }
  }, [cardBlob]);

  const handleDownload = useCallback(() => {
    if (!cardBlob) return;

    const url = URL.createObjectURL(cardBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `frenflow-stats-${stats.username}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [cardBlob, stats.username]);

  const getShareText = useCallback(() => {
    const pnlSign = stats.totalPnL >= 0 ? "+" : "";
    const roiSign = stats.roi >= 0 ? "+" : "";

    return `${t("share.stats.shareTextHeader")}

${t("share.stats.shareTextPortfolio")} $${(stats.portfolioValue ?? 0).toFixed(0)}
${t("share.stats.shareTextPnl")} ${pnlSign}$${(stats.totalPnL ?? 0).toFixed(0)} (${roiSign}${(stats.roi ?? 0).toFixed(1)}%)
${t("share.stats.shareTextVolume")} $${(stats.totalVolume / 1000).toFixed(1)}K
${t("share.stats.shareTextTrades")} ${stats.totalTrades}`;
  }, [stats, t]);

  const handleShareTwitter = useCallback(async () => {
    // First copy the image to clipboard
    if (cardBlob) {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({ "image/png": cardBlob }),
        ]);
      } catch (err) {
      }
    }

    // Then open Twitter
    const text = getShareText() + "\n\n" + t("share.stats.shareTextTwitterCta");
    const url = "https://frenflow.com";
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(twitterUrl, "_blank");

    // Show toast/alert to paste image
    setTimeout(() => {
      alert(t("share.stats.pasteHintTwitter"));
    }, 500);
  }, [getShareText, cardBlob, t]);

  const handleShareTelegram = useCallback(async () => {
    // First copy the image to clipboard
    if (cardBlob) {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({ "image/png": cardBlob }),
        ]);
      } catch (err) {
      }
    }

    const text = getShareText() + "\n\n" + t("share.stats.shareTextTelegramCta");
    const url = "https://frenflow.com";
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
    window.open(telegramUrl, "_blank");

    // Show toast/alert to paste image
    setTimeout(() => {
      alert(t("share.stats.pasteHintTelegram"));
    }, 500);
  }, [getShareText, cardBlob, t]);

  // Don't render if not open or if we're on the server
  if (!isOpen || typeof document === 'undefined') return null;

  // Use Portal to render outside parent stacking context
  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal - same size as SharePositionModal */}
      <div className="relative w-full max-w-2xl mx-4 bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-[#1A1A1A]">
          <h2 className="font-monument text-lg text-gray-900 dark:text-white">
            {t("share.stats.title")}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-[5px] text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#0f0f12] transition-colors"
          >
            <XIcon size={20} />
          </button>
        </div>

        {/* Period Tabs */}
        <div className="flex gap-2 px-4 pt-4">
          {([
            { key: 'today' as PeriodType, label: t("share.stats.period.today") },
            { key: 'week' as PeriodType, label: t("share.stats.period.week") },
            { key: 'month' as PeriodType, label: t("share.stats.period.month") },
            { key: 'all_time' as PeriodType, label: t("share.stats.period.all") },
          ]).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => handlePeriodChange(key)}
              disabled={loadingCard}
              className={`px-3 py-1.5 rounded-[5px] text-xs font-medium transition-colors ${
                selectedPeriod === key
                  ? 'bg-gray-900 dark:bg-white text-white dark:text-black'
                  : 'bg-gray-100 dark:bg-[#0f0f12] text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-[#1A1A1A]'
              } disabled:opacity-50`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Card Preview */}
        <div className="p-6">
          {loadingCard ? (
            <div className="h-[480px] rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] flex flex-col items-center justify-center gap-3">
              <div className="w-8 h-8 border-2 border-[#CCFF00]/30 border-t-[#CCFF00] rounded-full animate-spin" />
              <p className="font-satoshi text-sm text-gray-500 dark:text-gray-400">{t("share.stats.generating")}</p>
            </div>
          ) : error ? (
            <div className="h-[480px] rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] flex flex-col items-center justify-center gap-3">
              <p className="font-satoshi text-sm text-red-400">{error}</p>
              <button
                onClick={() => {
                  setLoadingCard(true);
                  setError(null);
                }}
                className="px-4 py-2 rounded-[5px] bg-[#CCFF00]/10 text-[#CCFF00] text-sm font-medium hover:bg-[#CCFF00]/20 transition-colors"
              >
                {t("share.stats.retry")}
              </button>
            </div>
          ) : cardImageUrl ? (
            <div className="relative" style={{ perspective: '1200px' }}>
              {/* 3D Card Container */}
              <div
                ref={cardRef}
                onMouseDown={handleCardMouseDown}
                onTouchStart={handleCardMouseDown}
                onDoubleClick={handleCardDoubleClick}
                className={`relative cursor-grab select-none ${isDragging ? 'cursor-grabbing' : ''}`}
                style={{
                  transformStyle: 'preserve-3d',
                  transform: `rotateX(${cardRotation.x}deg) rotateY(${cardRotation.y}deg)`,
                  transition: isDragging ? 'none' : 'transform 0.3s ease-out',
                }}
              >
                {/* Front of card */}
                <div
                  className="relative rounded-[5px] overflow-hidden"
                  style={{ backfaceVisibility: 'hidden' }}
                >
                  <img
                    src={cardImageUrl}
                    alt="Stats Card"
                    className="w-full h-auto rounded-[5px]"
                    draggable={false}
                  />
                  {/* Holographic shine effect */}
                  <div
                    className="absolute inset-0 rounded-[5px] pointer-events-none"
                    style={{
                      background: `linear-gradient(
                        ${135 + cardRotation.y}deg,
                        transparent 0%,
                        rgba(255,255,255,0.1) 45%,
                        rgba(255,255,255,0.3) 50%,
                        rgba(255,255,255,0.1) 55%,
                        transparent 100%
                      )`,
                      opacity: Math.abs(cardRotation.x) + Math.abs(cardRotation.y) > 5 ? 1 : 0,
                      transition: 'opacity 0.3s ease',
                    }}
                  />
                  {/* Rainbow edge glow */}
                  <div
                    className="absolute inset-0 rounded-[5px] pointer-events-none"
                    style={{
                      boxShadow: Math.abs(cardRotation.x) + Math.abs(cardRotation.y) > 10
                        ? `0 0 30px rgba(124, 58, 237, 0.4), 0 0 60px rgba(204, 255, 0, 0.3)`
                        : 'none',
                      transition: 'box-shadow 0.3s ease',
                    }}
                  />
                </div>

                {/* Back of card - matches generated card style */}
                <div
                  className="absolute inset-0 rounded-[5px] overflow-hidden"
                  style={{
                    backfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg)',
                    background: '#000',
                  }}
                >
                  {/* Inner card matching generated card style */}
                  <div
                    className="absolute inset-[1.5%] rounded-[5px] overflow-hidden flex flex-col items-center justify-center"
                    style={{
                      background: '#0f0f12',
                      border: '1px solid #1A1A1A',
                    }}
                  >
                  {/* Top accent line (gradient green→lime or red) */}
                  <div
                    className="absolute top-0 left-0 right-0 h-[2px]"
                    style={{
                      background: stats.totalPnL >= 0
                        ? 'linear-gradient(90deg, transparent 0%, #00ff80 30%, #CCFF00 50%, #00ff80 70%, transparent 100%)'
                        : 'linear-gradient(90deg, transparent 0%, #FF1744 30%, #FF6B6B 50%, #FF1744 70%, transparent 100%)',
                    }}
                  />
                  {/* Soft glow under accent line */}
                  <div
                    className="absolute top-0 left-0 right-0 h-[58px] pointer-events-none"
                    style={{
                      background: stats.totalPnL >= 0
                        ? 'linear-gradient(180deg, rgba(0, 255, 128, 0.05) 0%, transparent 100%)'
                        : 'linear-gradient(180deg, rgba(255, 23, 68, 0.05) 0%, transparent 100%)',
                    }}
                  />
                  {/* Holographic shine effect */}
                  <div
                    className="absolute inset-0 rounded-[5px] pointer-events-none"
                    style={{
                      background: `linear-gradient(
                        ${135 + cardRotation.y}deg,
                        transparent 0%,
                        rgba(255,255,255,0.06) 45%,
                        rgba(255,255,255,0.15) 50%,
                        rgba(255,255,255,0.06) 55%,
                        transparent 100%
                      )`,
                      opacity: Math.abs(cardRotation.x) + Math.abs(cardRotation.y) > 5 ? 1 : 0,
                      transition: 'opacity 0.3s ease',
                    }}
                  />
                  <img
                    src={stats.totalPnL >= 0 ? "/brand/frenflow_logo.png" : "/brand/frenflow_logo_red.png"}
                    alt="FrenFlow"
                    className="w-20 h-20 mb-5 relative z-10"
                  />
                  <p className="text-4xl relative z-10">
                    <span
                      className="font-monument font-bold"
                      style={{ color: stats.totalPnL >= 0 ? '#CCFF00' : '#FF1744' }}
                    >FREN</span>
                    <span className="font-satoshi font-bold text-white text-[34px]">FLOW</span>
                  </p>
                  <p className="text-sm font-satoshi text-[#9CA3AF] mt-2 relative z-10">
                    {t("share.stats.tagline")}
                  </p>
                  </div>
                </div>
              </div>

              {/* Hint text */}
              <div className="text-center mt-3">
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {t("share.stats.dragHint")}
                </span>
              </div>
            </div>
          ) : null}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 px-6 pb-6">
          {/* Copy Image */}
          <button
            onClick={handleCopyImage}
            disabled={copying || !cardBlob}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-[#0f0f12] hover:bg-gray-100 dark:hover:bg-[#1A1A1A] transition-colors font-satoshi text-xs font-semibold text-gray-900 dark:text-white disabled:opacity-50"
          >
            {copied ? (
              <>
                <FiCheck className="w-4 h-4 text-[#00FF9D]" />
                {t("share.stats.copied")}
              </>
            ) : (
              <>
                <CopyIcon size={16} />
                {t("share.stats.copy")}
              </>
            )}
          </button>

          {/* Download */}
          <button
            onClick={handleDownload}
            disabled={!cardBlob}
            className="flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-[#0f0f12] hover:bg-gray-100 dark:hover:bg-[#1A1A1A] transition-colors font-satoshi text-xs font-semibold text-gray-900 dark:text-white disabled:opacity-50"
          >
            <DownloadIcon size={16} />
          </button>

          {/* Share on X */}
          <button
            onClick={handleShareTwitter}
            disabled={!cardBlob}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-[5px] bg-black border border-gray-700 hover:border-gray-500 transition-colors font-satoshi text-xs font-semibold text-white disabled:opacity-50"
          >
            <FaXTwitter className="w-4 h-4" />
            X
          </button>

          {/* Telegram */}
          <button
            onClick={handleShareTelegram}
            disabled={!cardBlob}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-[5px] bg-[#0088cc] hover:bg-[#0077b5] transition-colors font-satoshi text-xs font-semibold text-white disabled:opacity-50"
          >
            <FaTelegram className="w-4 h-4" />
            Telegram
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
